import { motion } from "framer-motion";
import { ArrowRight } from "@phosphor-icons/react";

const SERVICES = [
    {
        index: "01",
        title: "20 FT Containers",
        copy: "Agile movement for city and short-haul lanes. Ideal for dense cargo, steel, machinery parts and palletised goods.",
        image: "https://images.unsplash.com/photo-1605732562742-3023a888e56e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxzaGlwcGluZyUyMGNvbnRhaW5lcnN8ZW58MHx8fHwxNzg3NjQzNzMwfDA&ixlib=rb-4.1.0&q=85&w=1400",
        alt: "Stacked orange shipping containers against a deep blue sky",
        span: "md:col-span-7",
    },
    {
        index: "02",
        title: "40 FT Containers",
        copy: "High-volume long-haul capacity for exports, imports and full-load distribution across national corridors.",
        image: "https://images.pexels.com/photos/14020705/pexels-photo-14020705.jpeg?auto=compress&cs=tinysrgb&w=1400",
        alt: "Busy cargo port with colorful containers at sunrise",
        span: "md:col-span-5",
    },
    {
        index: "03",
        title: "Heavy Machinery",
        copy: "Cranes, excavators and industrial plant moved on multi-axle trailers with permits and escorts managed end to end.",
        image: "https://images.unsplash.com/photo-1586191552066-d52dd1e3af86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxoZWF2eSUyMHRydWNrJTIwdHJhbnNwb3J0fGVufDB8fHx8MTc4NzY0MzczNXww&ixlib=rb-4.1.0&q=85&w=1400",
        alt: "Heavy truck hauling machinery on black asphalt",
        span: "md:col-span-5",
    },
    {
        index: "04",
        title: "Freight Forwarding",
        copy: "Our speciality. Documentation, customs coordination, warehousing and multi-modal routing — one partner, zero friction.",
        image: "https://images.pexels.com/photos/4487383/pexels-photo-4487383.jpeg?auto=compress&cs=tinysrgb&w=1400",
        alt: "Workers coordinating freight inside a spacious warehouse",
        span: "md:col-span-7",
    },
];

export default function Services() {
    return (
        <section id="services" className="px-5 py-24 md:px-10 md:py-36" data-testid="services-section">
            <motion.div
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="mb-16 flex flex-wrap items-end justify-between gap-6 md:mb-24"
            >
                <div>
                    <p className="mb-4 flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-blaze md:text-xs">
                        <span className="inline-block h-2 w-2 bg-blaze" />
                        What We Move
                    </p>
                    <h2 className="font-display text-4xl uppercase leading-[0.95] tracking-tight md:text-6xl">
                        Services built <br className="hidden md:block" />
                        <span className="text-outline-faint">for heavy work.</span>
                    </h2>
                </div>
                <p className="max-w-sm text-sm leading-relaxed text-white/50 md:text-base">
                    Every consignment gets a dedicated move plan — vehicle, route, permits and a single point of contact.
                </p>
            </motion.div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-12">
                {SERVICES.map((service, i) => (
                    <motion.article
                        key={service.title}
                        initial={{ opacity: 0, y: 48 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.8, delay: (i % 2) * 0.12, ease: [0.16, 1, 0.3, 1] }}
                        className={`group relative flex min-h-[420px] flex-col justify-end overflow-hidden border border-white/10 ${service.span}`}
                        data-testid={`service-card-${service.index}`}
                    >
                        <img
                            src={service.image}
                            alt={service.alt}
                            loading="lazy"
                            className="absolute inset-0 h-full w-full object-cover opacity-55 grayscale-[35%] transition-[opacity,transform,filter] duration-700 group-hover:scale-105 group-hover:opacity-75 group-hover:grayscale-0"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-void via-void/35 to-transparent" />
                        <span className="absolute right-6 top-6 font-display text-6xl text-white/15 transition-colors duration-300 group-hover:text-blaze md:text-7xl">
                            {service.index}
                        </span>
                        <div className="relative p-8 md:p-10">
                            <h3 className="mb-3 font-display text-3xl uppercase tracking-tight md:text-4xl">{service.title}</h3>
                            <p className="mb-6 max-w-md text-sm leading-relaxed text-white/65 md:text-base">{service.copy}</p>
                            <a
                                href="#enquiry"
                                data-testid={`service-cta-${service.index}`}
                                className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-blaze transition-colors duration-200 hover:text-white"
                            >
                                Enquire for this
                                <ArrowRight size={14} weight="bold" className="transition-transform duration-200 group-hover:translate-x-1" />
                            </a>
                        </div>
                    </motion.article>
                ))}
            </div>
        </section>
    );
}
